import { Component, EventEmitter, Output, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subject, Subscription } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { HttpService } from '~/core/services/http.service';
import { NotificationService } from '~/services/notification.service';
import { TabNavigationService } from '~/services/tab-navigation.service';
import { ConfirmationService } from '~/services/confirmation.service';

@Component({
  selector: 'app-bulk-upload',
  templateUrl: './bulk-upload.component.html',
  styleUrls: ['./bulk-upload.component.scss'],
  standalone: false
})
export class BulkUploadComponent implements OnInit, OnDestroy {
  @Output() fileSelected = new EventEmitter<File>();
  @Output() backRequested = new EventEmitter<void>();
  @Input() isUploading: boolean = false;
  @Input() uploadType: 'excel' | 'raw' | null = null;
  
  selectedFile: File | null = null;
  isDragOver = false;
  isDownloadingTemplate: boolean = false;
  
  // Upload status tracking
  currentJobId: string | null = null;
  uploadStatus: any = null;
  statusMessage: string = '';
  progressPercentage: number = 0;
  
  // Validation error tracking
  validationErrors: any[] = [];
  showValidationErrorsModal: boolean = false;
  isValidationFailed: boolean = false;
  
  // Polling
  private pollingSubscription: Subscription | null = null;
  private countdownSubscription: Subscription | null = null;
  private destroy$ = new Subject<void>();
  validationCompletedShown: boolean | undefined;
  
  // Countdown for redirect
  countdownSeconds: number = 0;

  constructor(
    private httpService: HttpService,
    private router: Router,
    private notificationService: NotificationService,
    private tabNavigationService: TabNavigationService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    // Check if user is on /upload route after page refresh
    // If uploadType is not selected (null in service), user likely refreshed the page
    const selectedUploadType = this.tabNavigationService.getSelectedUploadType();
    if (!selectedUploadType) {
      console.warn('[BulkUploadComponent] Page refresh detected on /upload. Redirecting to /wells');
      this.router.navigate(['/wells']);
      return;
    }
    
    // Subscribe to upload type changes from service
    this.tabNavigationService.selectedUploadType$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(type => {
      this.uploadType = type;
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFileSelect(event: Event): void {
    if (this.isUploading) return; // Prevent selection during upload
    
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.handleFile(file);
    }
  }

  onDragOver(event: DragEvent): void {
    if (this.isUploading) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    if (this.isUploading) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      this.handleFile(file);
    }
  }

  private handleFile(file: File): void {
    // Determine valid extensions based on upload type
    const validExtensions = this.uploadType === 'excel' ? ['.xlsx', '.xls'] : ['.zip'];
    const fileTypeLabel = this.uploadType === 'excel' ? 'Excel' : 'ZIP';
    const allowedFormats = this.uploadType === 'excel' ? '.xlsx or .xls' : '.zip';
    
    const fileName = file.name.toLowerCase();
    const isValid = validExtensions.some(ext => fileName.endsWith(ext));

    if (!isValid) {
      this.confirmationService.show({
        title: 'Invalid File',
        message: `Please select a valid ${fileTypeLabel} file (${allowedFormats})`,
        confirmText: 'OK',
        cancelText: '',
        type: 'info',
        confirmCallback: () => {},
        cancelCallback: () => {}
      });
      return;
    }

    this.selectedFile = file;
    // Store file but don't upload yet - wait for user to click Validate & Upload button
  }

  /**
   * Start the upload process
   */
  /**
   * Validate and upload file
   */
  validateAndUpload(): void {
    if (!this.selectedFile) {
      this.confirmationService.show({
        title: 'No File Selected',
        message: 'Please select a file first',
        confirmText: 'OK',
        cancelText: '',
        type: 'info',
        confirmCallback: () => {},
        cancelCallback: () => {}
      });
      return;
    }
    this.startUpload(this.selectedFile);
  }

  /**
   * Show validation errors modal
   */
  showValidationErrors(): void {
    this.showValidationErrorsModal = true;
  }

  /**
   * Close validation errors modal
   */
  closeValidationErrorsModal(): void {
    this.showValidationErrorsModal = false;
  }

  /**
   * Retry upload with same file
   */
  retryUpload(): void {
    if (this.selectedFile) {
      this.isValidationFailed = false;
      this.validationErrors = [];
      this.validationCompletedShown = false;
      this.showValidationErrorsModal = false;
      this.startUpload(this.selectedFile);
    }
  }

  private startUpload(file: File): void {
    const fileName = file.name;
    this.isUploading = true;
    this.isValidationFailed = false;
    this.validationErrors = [];
    this.statusMessage = `Validating ${fileName}...`;
    this.progressPercentage = 0;

    // Call appropriate upload method based on upload type
    const uploadObservable = this.uploadType === 'raw' 
      ? this.httpService.uploadRawFile(file)
      : this.httpService.uploadExcelFile(file);

    uploadObservable.subscribe({
      next: (response: any) => {
        console.log('Upload initiated with job ID:', response.job_id);
        this.currentJobId = response.job_id;
        this.uploadStatus = response;
        
        // Start polling for status
        this.startPolling();
      },
      error: (err: any) => {
        console.error('Upload failed:', err);
        this.isUploading = false;
        this.statusMessage = '';
        this.notificationService.error('Upload failed: ' + (err.message || 'Unknown error'));
      }
    });
  }

  /**
   * Start polling the upload status every 5 seconds
   */
  private startPolling(): void {
    if (!this.currentJobId) {
      console.error('Cannot start polling: no job ID');
      return;
    }

    // Poll every 5 seconds
    this.pollingSubscription = interval(5000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.httpService.getUploadStatus(this.currentJobId!))
      )
      .subscribe({
        next: (status: any) => {
          this.uploadStatus = status;
          console.log('Upload status:', status.status, '-', status.message);
          
          // Update progress display
          if (status.progress) {
            this.progressPercentage = status.progress.percentage || 0;
          }
          
          // Update status message with more intuitive text
          this.updateStatusMessage(status);
          
          // Check if upload is completed
          if (status.status === 'completed') {
            console.log('Upload completed successfully');
            this.stopPolling();
            this.handleUploadComplete(status);
          } else if (status.status === 'failed' || status.status === 'error') {
            console.error('Upload failed:', status.message);
            this.stopPolling();
            this.handleUploadError(status);
          }
        },
        error: (err: any) => {
          console.error('Error polling upload status:', err);
          this.stopPolling();
          this.isUploading = false;
          this.statusMessage = '';
          this.notificationService.error('Error checking upload status: ' + (err.message || 'Unknown error'));
        }
      });
  }

  /**
   * Update the status message based on current upload status
   */
  private updateStatusMessage(status: any): void {
    const progress = status.progress;
    
    if (!progress) {
      this.statusMessage = status.message || 'Processing...';
      return;
    }

    const stage = progress.current_stage || 'processing';
    const percentage = progress.percentage || 0;
    const wellsProcessed = progress.wells_processed || 0;
    const totalWells = progress.total_wells || 0;

    // Provide intuitive messages based on current stage
    switch (stage.toLowerCase()) {
      case 'validating':
      case 'validating_data':
        if (percentage === 100 && !this.validationCompletedShown) {
          // Validation just completed
          this.validationCompletedShown = true;
          this.notificationService.success('Validation successful! Starting upload...');
          this.statusMessage = `Uploading ${this.selectedFile?.name || 'file'}...`;
        } else if (percentage < 100) {
          this.statusMessage = `Validating file... (${percentage}%)`;
        }
        break;
      case 'uploading_to_adls':
        this.statusMessage = `Uploading data: Well ${wellsProcessed}/${totalWells} (${percentage}%)`;
        break;
      case 'processing':
        this.statusMessage = `Processing data... (${percentage}%)`;
        break;
      case 'completed':
        this.statusMessage = `Upload completed successfully!`;
        break;
      default:
        this.statusMessage = status.message || `Processing... (${percentage}%)`;
    }
  }

  /**
   * Handle successful upload completion
   */
  private handleUploadComplete(status: any): void {
    this.isUploading = false;
    const wellsCount = status.progress?.wells_processed || 0;
    
    console.log('[handleUploadComplete] Upload completed, closing modal');

    // Ensure wells list refreshes once upload processing is complete
    this.tabNavigationService.setActiveTab('wells');
    this.tabNavigationService.triggerResetWells();
    
    // Close the upload modal to go back to wells view
    this.tabNavigationService.closeUploadModal();
    
    // Start countdown from 5 seconds
    this.countdownSeconds = 5;
    
    // Show initial success notification
    this.notificationService.success(`Upload successful! ${wellsCount} well(s) processed. Navigating back to wells page in... ${this.countdownSeconds}`);
    
    // Start countdown timer
    this.countdownSubscription = interval(1000)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.countdownSeconds--;
        
        if (this.countdownSeconds > 0) {
          // Update notification with new countdown value
          this.notificationService.success(`Upload successful! ${wellsCount} well(s) processed. Navigating back to wells page in ${this.countdownSeconds}...`);
        } else {
          // Countdown finished, navigate to wells
          if (this.countdownSubscription) {
            this.countdownSubscription.unsubscribe();
            this.countdownSubscription = null;
          }
          this.router.navigate(['/wells']);
        }
      });
  }

  /**
   * Handle upload error
   */
  private handleUploadError(status: any): void {
    this.isUploading = false;
    this.statusMessage = '';
    this.isValidationFailed = true;
    
    // Try multiple paths to get validation errors
    // Priority: validation_errors > result?.errors > errors
    let errorsList: any[] = [];
    
    if (Array.isArray(status.validation_errors) && status.validation_errors.length > 0) {
      errorsList = status.validation_errors;
    } else if (Array.isArray(status.result?.errors) && status.result.errors.length > 0) {
      errorsList = status.result.errors;
    } else if (Array.isArray(status.errors) && status.errors.length > 0) {
      errorsList = status.errors;
    }
    
    console.log('Final errorsList:', errorsList, 'Length:', errorsList.length);
    
    if (errorsList.length > 0) {
      // Transform validation errors to user-friendly format
      // Errors can have multiple formats, so we need to handle both simple errors and detailed validation errors
      this.validationErrors = errorsList.map(err => {
        // Check if this is a detailed validation error with sheet_name and missing_columns
        if (err.type === 'missing_columns' || err.sheet_name) {
          return {
            type: err.type || 'validation_error',
            sheet_name: err.sheet_name,
            missing_columns: err.missing_columns || [],
            error_message: err.error || 'Missing required columns',
            // Keep original error for debugging
            _raw: err
          };
        }
        // Simple error format
        return {
          type: 'error',
          message: typeof err === 'string' ? err : (err.message || err.error || JSON.stringify(err)),
          _raw: err
        };
      });
      
      console.log('Transformed validationErrors:', this.validationErrors);
      
      // Show snackbar notification with error count
      const errorCount = errorsList.length;
      const errorMessage = `Validation failed with ${errorCount} error${errorCount !== 1 ? 's' : ''}.`;
      this.notificationService.error(errorMessage);
    } else {
      // Show generic error message if no detailed errors
      const errorMessage = status.message || 'Upload processing failed';
      this.notificationService.error(errorMessage);
      this.isValidationFailed = false;
    }
  }

  /**
   * Stop polling the upload status
   */
  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  openFileDialog(): void {
    if (this.isUploading) return;
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    fileInput?.click();
  }

  goBack(): void {
    if (this.isUploading) return; // Prevent going back during upload
    this.backRequested.emit();
  }

  clearSelectedFile(): void {
    this.selectedFile = null;
    this.validationErrors = [];
    this.isValidationFailed = false;
    this.validationCompletedShown = false;
    this.showValidationErrorsModal = false;
  }



  /**
   * Get formatted error display text for the UI
   */
  getErrorDisplayText(error: any): string {
    if (!error) return 'Unknown error';
    
    // Handle detailed validation errors with sheet_name and missing_columns
    if (error.sheet_name && error.missing_columns) {
      const columns = Array.isArray(error.missing_columns) 
        ? error.missing_columns.join(', ') 
        : error.missing_columns;
      return `Sheet "${error.sheet_name}": Missing columns - ${columns}`;
    }
    
    // Handle simple error message format
    if (error.message) {
      return error.message;
    }
    
    // Fallback
    return error.error_message || JSON.stringify(error);
  }

  /**
   * Check if an error has detailed information to display
   */
  hasDetailedErrorInfo(error: any): boolean {
    return error.sheet_name && Array.isArray(error.missing_columns) && error.missing_columns.length > 0;
  }

  /**
   * Clear everything and prepare for new upload
   */
  uploadNew(): void {
    this.selectedFile = null;
    this.validationErrors = [];
    this.isValidationFailed = false;
    this.validationCompletedShown = false;
    this.showValidationErrorsModal = false;
    this.currentJobId = null;
    this.uploadStatus = null;
    this.statusMessage = '';
    this.progressPercentage = 0;
  }

  downloadTemplate(): void {
    if (this.isUploading || this.isDownloadingTemplate) return;
    
    this.isDownloadingTemplate = true;
    
    // Call API to get the template file details
    this.httpService.getFileTemplateSample().subscribe({
      next: (response: any) => {
        const sasUrl = response.sas_url;
        const fileName = response.file_name || 'template.xlsx';
        
        // Download the file using the SAS URL
        this.downloadFileFromUrl(sasUrl, fileName);
        this.isDownloadingTemplate = false;
        
        // Show success notification
        this.notificationService.success(`Template downloaded successfully: ${fileName}`);
      },
      error: (err: any) => {
        console.error('Error getting template:', err);
        this.notificationService.error('Failed to download template. Please try again.');
        this.isDownloadingTemplate = false;
      }
    });
  }

  private downloadFileFromUrl(url: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
